import Swal from 'sweetalert2';
import { ReturnCommand } from '../../../../../services/command';
import { ReturnQuery } from '../../../../../services/query';
import Helper from '../../../../../utils/helper';

type LabelDeps = {
    returnId: any;
    customerId: any;
    setRefresh?: any;
    t: (key: string) => string;
};

const LABEL_ERROR_TRANSLATION_KEYS: Record<string, string> = {
    RETURN_ID_REQUIRED: 'errors.RETURN_ID_REQUIRED',
    LABEL_DETAILS_NOT_FOUND: 'errors.LABEL_DETAILS_NOT_FOUND',
    LABEL_TEMPLATE_GENERATION_FAILED: 'errors.LABEL_TEMPLATE_GENERATION_FAILED',
};

const showPreparingLabel = (message: string) => {
    Swal.fire({
        text: message,
        icon: 'info',
        timer: 5000,
        timerProgressBar: true,
        didOpen: () => {
            Swal.showLoading();
        },
    });
};

const parseErrorPayload = async (data: any): Promise<any> => {
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try {
            const text = await data.text();
            if (!text) {
                return '';
            }
            try {
                return JSON.parse(text);
            } catch {
                return text;
            }
        } catch {
            return '';
        }
    }
    return data;
};

const extractErrorCodeOrMessage = (data: any): string => {
    if (typeof data === 'string') {
        return data;
    }
    if (data && typeof data === 'object') {
        return data.message || data.detail || data.title || data.error || data.code || '';
    }
    return '';
};

const resolveLabelErrorMessage = (data: any, t: (key: string) => string): string => {
    const codeOrMessage = extractErrorCodeOrMessage(data);
    if (codeOrMessage && LABEL_ERROR_TRANSLATION_KEYS[codeOrMessage]) {
        return t(LABEL_ERROR_TRANSLATION_KEYS[codeOrMessage]);
    }
    if (codeOrMessage) {
        return codeOrMessage;
    }
    return t('general.serviceError');
};

const handleLabelError = async (
    error: any,
    retryCount: number,
    maxRetry: number,
    deps: LabelDeps,
    retry: () => void
) => {
    const { t } = deps;
    const status = error?.status;
    const errorData = await parseErrorPayload(error?.data);
    const displayMessage = resolveLabelErrorMessage(errorData, t);

    switch (status) {
        case 401:
            Swal.fire(t('general.sessionExpiredError'), '', 'error');
            break;
        case 403:
            Swal.fire(t('general.authorizationError'), '', 'error');
            break;
        case 404:
        case 500:
            if (retryCount < maxRetry) {
                showPreparingLabel(t('general.preparingShippingLabel'));
                setTimeout(retry, 5000);
            } else {
                Swal.fire(t('general.error'), displayMessage || t('general.maxRetryReached'), 'error');
            }
            break;
        case 400:
            Swal.fire({
                icon: 'error',
                title: displayMessage,
                showConfirmButton: true,
            });
            break;
        default:
            Swal.fire(t('general.error'), displayMessage, 'error');
            break;
    }
};

export const fetchRejectedLabelAndUpdateStatus = async (
    retryCount: number,
    maxRetry: number,
    deps: LabelDeps,
    retry: () => void
) => {
    const { returnId, customerId, setRefresh } = deps;
    try {
        await ReturnQuery.GetRejectedLabelDetails({ returnId });
        const req: any = {
            storeCode: Helper.getStoreCode(),
            returnId: Number(returnId),
            status: 15,
            customerId: Number(customerId),
        };
        const res: any = await ReturnCommand.UpdateReturnStatus(req);
        if (res.result) {
            Swal.fire('', ' ', 'success');
            setRefresh?.((prevLoading: any) => !prevLoading);
        }
    } catch (error: any) {
        await handleLabelError(error, retryCount, maxRetry, deps, retry);
    }
};
